<template>
	<div>
		<v-snackbar v-model="goalDeletedNotif" top>
			Goal Deleted Successfully
			<v-btn color="pink" flat @click="goalDeletedNotif = false">Close</v-btn>
		</v-snackbar>

		<v-snackbar v-model="goalSavedNotif" top>
			Goal Saved
			<v-btn color="pink" flat @click="goalSavedNotif = false">Close</v-btn>
		</v-snackbar>

		<v-snackbar v-model="snackbar.show" top :color="snackbar.color" :timeout="snackbar.timeout">
			{{ snackbar.message }}
			<v-btn :color="snackbar.buttonColor" flat @click="snackbar.show = false"> Close </v-btn>
		</v-snackbar>

		<div v-if="employeeLoaded">
			<div v-if="hasAccess" class="profile-container">
				<v-layout class="profile-header">
					<v-flex style="width: 200px" class="shrink px-5 pt-3">
						<image-input v-model="avatar" @input="uploadImage">
							<div slot="activator">
								<v-avatar
									v-ripple
									style="margin-top: 3px; margin-bottom: 3px; z-index: 1; cursor: pointer; background-position: center center; background-repeat: no-repeat"
									:size="110"
									@mouseover="avatarHover = true"
									@mouseleave="avatarHover = false"
								>
									<img :src="avatar.imageURL" alt="avatar" class="avatar-img" style="border-color: white; border-width: 2px; border-style: solid" />

									<div v-show="avatarHover" class="avatar-hover">
										<span v-if="employee.avatarId == null" class="caption">Click to add {{ $term('employee') }} photo</span>
										<span v-else class="caption">Click to change {{ $term('employee') }} photo</span>
									</div>
								</v-avatar>
							</div>
						</image-input>
					</v-flex>

					<v-flex class="pt-3 text-xs-left">
						<div class="noPrint">
							<h4 class="headline mb-0" style="color: #fff">
								{{ fullName }}
								<v-chip v-if="form.status !== 'active'" color="error" dark class="ml-1" small>Inactive</v-chip>
							</h4>
							<h5 style="color: #fff" v-if="employeeLoaded && employee.currentPosition != null">{{ employee.currentPosition.title }}</h5>
						</div>
					</v-flex>
					<v-flex shrink>
						<v-menu bottom left v-if="canSchedulePerformanceReview || canMakeAdmin || canRevokeAdmin || canTerminateEmployee">
							<template v-slot:activator="{ on }">
								<v-btn dark small outline v-on="on" class="mr-4"> Actions<v-icon class="ml-1">mdi-chevron-down</v-icon> </v-btn>
							</template>

							<v-list>
								<v-list-tile v-if="canSchedulePerformanceReview" @click="schedulePerformanceReview">
									<v-list-tile-title>Schedule a Performance Review</v-list-tile-title>
								</v-list-tile>
								<v-list-tile v-if="canMakeAdmin" @click="showMakeAdminDialog = true">
									<v-list-tile-title>Make Admin</v-list-tile-title>
								</v-list-tile>
								<v-list-tile v-if="canRevokeAdmin" @click="showRevokeAdminDialog = true">
									<v-list-tile-title>Revoke Admin Privileges</v-list-tile-title>
								</v-list-tile>
								<v-list-tile v-if="canTerminateEmployee" @click="showConfirmTerminateDialog = true">
									<v-list-tile-title class="red--text">Terminate {{ $term('employee', { case: 'title' }) }}</v-list-tile-title>
								</v-list-tile>
							</v-list>
						</v-menu>
					</v-flex>
				</v-layout>

				<v-layout class="profile-body" style="display: -webkit-box;">
					<v-flex class="shrink text-xs-left px-3 profile-body-sidebar">
						<div v-if="employeeLoaded">
							<table role="presentation">
								<tr v-if="emailAddress" class="mb-1">
									<td style="width: 1px">
										<v-icon style="margin-right: 5px; font-size: 1.1em">mdi-email-outline</v-icon>
									</td>
									<td>
										<a class="caption" :href="`mailto:${emailAddress}`">{{ emailAddressSnipped }}</a>
									</td>
								</tr>
								<tr v-if="department" class="mb-1">
									<td style="width: 1px">
										<v-icon style="margin-right: 5px; font-size: 1.1em">mdi-account-group-outline</v-icon>
									</td>
									<td class="caption">
										{{ department }}
									</td>
								</tr>
								<tr class="mb-1">
									<td style="width: 1px" class="caption">
										<span v-if="hasLogin"><v-icon class="green--text" style="margin-right: 5px; font-size: 1.1em">mdi-check-circle-outline</v-icon></span>
										<span v-else> <v-icon class="red--text" style="margin-right: 5px; font-size: 1.1em">mdi-close-circle-outline</v-icon></span>
									</td>
									<td class="caption">
										<span v-if="hasLogin">Has access<v-chip label small class="ml-2" dark color="success" v-if="hasLogin && form.isAdmin">Admin</v-chip></span>
										<span v-else>Does not have access</span>
									</td>
								</tr>
								<tr v-if="isHiddenFromEmployeeDirectory" class="mb-1">
									<td style="width: 1px">
										<v-icon style="margin-right: 5px; font-size: 1.1em">mdi-eye-off-outline</v-icon>
									</td>
									<td class="caption">
										<v-tooltip bottom :max-width="250">
											<template v-slot:activator="{ on }">
												<span v-on="on">Hidden from {{ $term('employee', { case: 'title' }) }} Directory</span>
											</template>
											<span>{{ firstName }} will not show up in any reports, searches, or selections.</span>
										</v-tooltip>
									</td>
								</tr>
							</table>

							<div v-if="reportsTo">
								<v-divider class="mt-4 mb-1" />

								<v-subheader>Manager</v-subheader>

								<v-layout>
									<v-flex shrink>
										<v-avatar style="margin-top: 3px; margin-bottom: 3px" :size="30" color="lighten-4">
											<img :src="getAvatar(reportsTo.id)" alt="avatar" class="avatar-img" />
										</v-avatar>
									</v-flex>
									<v-flex class="ml-2 caption">
										<router-link :to="{ name: 'employee', params: { id: reportsTo.id } }">{{ reportsTo.fullName }}</router-link>
										<div>{{ reportsTo.positionTitle }}</div>
									</v-flex>
								</v-layout>
							</div>

							<div v-if="tags && tags.length > 0">
								<v-divider class="mt-4" />

								<v-subheader>Tags</v-subheader>

								<tag-items :tags="tags" read-only />
							</div>
						</div>
					</v-flex>

					<v-flex>
						<v-tabs ref="tabs" v-model="tab" grow slider-color="white" color="transparent" show-arrows style="margin-top: -48px" >
							<v-tab style="color: #fff">Details</v-tab>
							<v-tab style="color: #fff" v-if="$_hasAccessToModule('compliance')">Compliance</v-tab>
							<v-tab style="color: #fff" v-if="$_hasAccessToModule('positionDescriptions')">{{ $term('positionDescription', { case: 'title' }) }} ({{ $term('positionDescription', { abbreviate: true, case: 'upper' }) }})</v-tab>
							<v-tab style="color: #fff" v-if="$_hasAccessToModule('goals')" class="noPrint">Goals</v-tab>
							<v-tab v-if="isFormsEnabled" href="#forms" style="color: #fff" class="noPrint">Forms</v-tab>
							<v-tab style="color: #fff" class="noPrint">Files</v-tab>
							<v-tab style="color: #fff" class="noPrint">Assets</v-tab>
							<v-tab v-if="isCertificationsEnabled && $_hasAccessToModule('compliance')" href="#certifications" style="color: #fff" class="noPrint">Certifications</v-tab>

							<v-tab-item>
								<v-card flat>
									<v-card-text>
										<employee-details-editor v-if="form" ref="employeeDetailsEditor" v-model="form.employeeDetails" :employee-id="id" :employee-status="form.status" :forms="employeeForms" persist-changes></employee-details-editor>
									</v-card-text>
								</v-card>
							</v-tab-item>
							<v-tab-item v-if="$_hasAccessToModule('compliance')" class="profile-tab">
								<v-card flat>
									<v-card-text>
										<employee-compliance v-if="!employee.hideFromEmployeeDirectory" :employeeId="employee.id" :trigger-refresh-id="employeeComplianceRefreshId" @onUpdated="onComplianceUpdated" />
										<v-alert v-else :value="true" class="text-xs-left" color="info" outline icon="mdi-eye-off-outline"
											>This {{ $term('employee') }} is hidden from {{ $term('employee') }} directory. Compliance items are only required for active {{ $term('employee', { plural: true }) }}.</v-alert
										>
									</v-card-text>
								</v-card>
							</v-tab-item>

							<v-tab-item v-if="$_hasAccessToModule('positionDescriptions') && employee !== null" class="profile-tab">
								<v-card flat>
									<v-card-text>
										<div v-if="!employee.positions || employee.positions.length === 0" class="text-xs-left">
										<v-subheader class="pl-1">This {{ $term('employee') }} does not yet have a position setup</v-subheader>
											<v-btn v-if="canEdit" color="primary" @click="onAddPosition">Add Position</v-btn>
										</div>

										<div v-else class="text-xs-right">
											<v-btn v-if="canEdit" class="mb-3" @click="onAddPosition">Add Position</v-btn>

											<employee-position-list
												:employee-id="employee.id"
												:positions="employee.positions"
												:loggedInEmployee="loggedInEmployee"
												@onEditPosition="onEditPosition"
												@onDeletePosition="onDeletePosition"
												@onApprovePosition="onApprovePosition"
												@onSendPosition="onSendPosition"
												@onSendPositionToEmployee="onSendPositionToEmployee"
											>
											</employee-position-list>
										</div>
									</v-card-text>
								</v-card>
							</v-tab-item>

							<v-tab-item v-if="$_hasAccessToModule('goals') && employee !== null" class="profile-tab">
								<employee-goal-list
									:employee-id="id"
									:goals="goals"
									:show-header="false"
									flat
									hideFooter
									showAddButtonAboveContent
									@onGoalAdded="onGoalAdded"
									@onGoalEdited="onGoalEdited"
									@onGoalDeleted="onGoalDeleted"
								></employee-goal-list>
							</v-tab-item>

							<v-tab-item v-if="isFormsEnabled && employee !== null" value="forms" class="profile-tab">
								<employee-assigned-forms-list
									ref="employeeAssignedFormsList"
									:forms="assignedForms"
									:available-forms="availableForms"
									:employees="employees"
									:employee-id="id"
									:employee-manager-id="employee ? employee.reportsTo : null"
									:employee-manager-name="reportsTo ? reportsTo.fullName : ''"
									:is-loading="isListingAssignedForms"
									:is-assigning="isAssigningForm"
									:is-sending-new-link="isSendingNewFormLink"
									:is-cancelling-form-submission="isCancellingFormSubmission"
									:can-assign="canEdit"
									:employee-name="fullName"
									:employee-first-name="firstName"
									:tenant-id="loggedInTenant.id"
									:is-employee-respondent="isLoggedInEmployee"
									@openAssign="ensureFormsLoaded"
									@assign="assignFormToEmployee"
									@sendNewLink="sendNewFormLink"
									@cancelSubmission="cancelFormSubmission"
									@openSubmission="openEmbeddedSubmission"
								/>
							</v-tab-item>

							<v-tab-item v-if="employee !== null" class="profile-tab">
								<v-card flat>
									<v-card-text>
										<div v-if="employeeLoaded && employee.files.length > 0 && canEdit" class="text-xs-right mb-3">
											<v-btn @click="onUploadFileClick">Add a File</v-btn>
										</div>

										<v-list v-if="employeeLoaded && employee.files.length > 0" two-line subheader>
											<v-list-tile v-for="file in employee.files" :key="file.id" avatar>
												<v-list-tile-avatar>
													<v-icon class="blue white--text">assignment</v-icon>
												</v-list-tile-avatar>

												<v-list-tile-content>
													<v-list-tile-title>{{ file.file.fileName }}</v-list-tile-title>
													<v-list-tile-sub-title>
														Uploaded
														<AppDate :date="file.file.createdDateUTC" />
													</v-list-tile-sub-title>
												</v-list-tile-content>

												<v-list-tile-action>
													<v-tooltip bottom>
														<v-btn slot="activator" icon ripple @click="downloadFile(file.id, file.file.fileName)">
															<v-icon color="grey">get_app</v-icon>
														</v-btn>
														<span>Download File</span>
													</v-tooltip>
												</v-list-tile-action>

												<v-list-tile-action v-if="canEdit">
													<v-tooltip bottom>
														<v-btn slot="activator" icon ripple @click="onDeleteFile(file.id)">
															<v-icon color="grey">delete</v-icon>
														</v-btn>
														<span>Remove File</span>
													</v-tooltip>
												</v-list-tile-action>
											</v-list-tile>
										</v-list>
										<div v-else class="text-xs-left">
										<v-subheader class="pl-1">This {{ $term('employee') }} does not have any files</v-subheader>
											<v-btn v-if="canEdit" color="primary" @click="onUploadFileClick">Add a File</v-btn>
										</div>
									</v-card-text>
								</v-card>
							</v-tab-item>
							<v-tab-item>
								<v-card flat class="pa-3">
									<assets-list :employee-id="employee.id" hide-actions display-unassign-btn />
								</v-card>
							</v-tab-item>
							<v-tab-item value="certifications">
								<certification-for-employee :employee-id="id" :certification-id="certificationId"/>
							</v-tab-item>
						</v-tabs>

					</v-flex>
				</v-layout>
			</div>

			<div v-else>You do not have access to this resource</div>
		</div>

		<employee-position-editor
			v-if="showPositionEditor"
			:dialog="showPositionEditor"
			:title="editorTitle"
			:position="editedPosition"
			:position-templates="positionTemplates"
			:employees="employees"
			:employee-id="id"
			:is-admin="loggedInEmployee.isAdmin"
			:is-saving="isSavingPosition"
			:requireEmployeeAcknowledgement="requireEmployeeAcknowledgement"
			@save="savePosition"
			@closeEditor="showPositionEditor = false"
			@convertPositionToTemplate="convertPositionToTemplate"
		></employee-position-editor>

		<loading-indicator v-if="isLoading" :loading-text="`Loading ${$term('employee', { case: 'title' })}...`" />

		<confirmation-dialog
			v-if="showConfirmDeletePositionDialog"
			:dialog="showConfirmDeletePositionDialog"
			:text="`Are you sure you want to delete this ${$term('positionDescription')}?`"
			:isConfirming="isDeletingPosition"
			@onConfirm="deletePosition"
			@onCancel="showConfirmDeletePositionDialog = false"
		/>

		<confirmation-dialog v-if="showConfirmTerminateDialog" :dialog="showConfirmTerminateDialog" title="Confirm Termination" :isConfirming="isTerminatingEmployee" @onConfirm="terminateEmployee" @onCancel="showConfirmTerminateDialog = false">
			<div class="mt-2">Are you sure you want to terminate this {{ $term('employee') }}?</div>
			<v-alert :value="true" type="warning" class="mt-4 text-xs-left" outline>
				Please note: This {{ $term('employee') }} will no longer have access to the system.
			</v-alert>
		</confirmation-dialog>

		<confirmation-dialog v-if="showMakeAdminDialog" :dialog="showMakeAdminDialog" title="Make Admin" :isConfirming="isSettingAdminPrivileges" @onConfirm="makeAdmin" @onCancel="showMakeAdminDialog = false">
			<div class="mt-2 mb-4">Are you sure you want to make this {{ $term('employee') }} an admin?</div>
			<v-alert :value="true" color="warning" icon="warning" class="text-xs-left" outline>Administrators have full system access. Please grant this privilege with caution.</v-alert>
		</confirmation-dialog>

		<confirmation-dialog v-if="showRevokeAdminDialog" :dialog="showRevokeAdminDialog" title="Revoke Admin Privileges" :isConfirming="isSettingAdminPrivileges" @onConfirm="revokeAdminPrivileges" @onCancel="showRevokeAdminDialog = false">
			<div class="mt-2 mb-4">Are you sure you want to revoke admin privileges from this {{ $term('employee') }}?</div>
		</confirmation-dialog>

		<approve-position-description-dialog
			:dialog="showApprovePositionDialog"
			:isApproving="isApprovingPosition"
			:requireEmployeeAcknowledgement="requireEmployeeAcknowledgement"
			@onApprove="approvePosition"
			@onCancel="showApprovePositionDialog = false" />

		<send-email-dialog
			v-if="showSendPositionToEmployeeDialog"
			:dialog="showSendPositionToEmployeeDialog"
			:title="`Send to ${$term('employee', { case: 'title' })}`"
			:recipients="[$_mapEmployeeToEmailRecipientModel(employee)]"
			:subject="$_getEmailMetaAfterPositionApproval(loggedInEmployee, employee, positionToSend, requireEmployeeAcknowledgement).subject"
			:body="$_getEmailMetaAfterPositionApproval(loggedInEmployee, employee, positionToSend, requireEmployeeAcknowledgement).body"
			@onCancel="showSendPositionToEmployeeDialog = false"
			@onSent="onPositionDescriptionSentToEmployee"
		>
			<template v-slot:header>
				<div v-if="positionDescriptionJustApproved">
					<v-alert class="text-xs-left" :value="true" outline type="success">{{ positionApprovedMessage }} Would you like to send a copy to the {{ $term('employee') }}?</v-alert>
					<div class="text-xs-right mb-4" style="margin-top: -3px"><a @click="showSendPositionToEmployeeDialog = false">Cancel</a></div>
				</div>
			</template>
			<template v-slot:below-body>
				<div class="info-text" style="font-style: normal;">
					<span class="mr-1">{{ $term('positionDescription', { case: 'title' }) }} link</span><v-icon small>link</v-icon> <br /><a :href="$_getPositionDescriptionLink(employee, positionToSend)" target="_blank">{{
						$_getPositionDescriptionLink(employee, positionToSend)
					}}</a>
				</div>
			</template>
		</send-email-dialog>

		<send-email-dialog
			v-if="showSendPositionDialog"
			:dialog="showSendPositionDialog"
			:title="`Email ${$term('positionDescription', { case: 'title' })}`"
			:subject="$_getEmailMeta(loggedInEmployee, employee, positionToSend).subject"
			:body="$_getEmailMeta(loggedInEmployee, employee, positionToSend).body"
			@onCancel="showSendPositionDialog = false"
			@onSent="onPositionDescriptionSent"
		>
			<template v-slot:below-body>
				<div class="info-text" style="font-style: normal;">
					<span class="mr-1">{{ $term('positionDescription', { case: 'title' }) }} link</span><v-icon small>link</v-icon> <br /><a :href="$_getPositionDescriptionLink(employee, positionToSend)" target="_blank">{{
						$_getPositionDescriptionLink(employee, positionToSend)
					}}</a>
				</div>
			</template>
		</send-email-dialog>

		<file-upload 
		v-if="uploadFileDialog"
		:dialog="uploadFileDialog" 
		title="Upload File" 
		@onSecondaryAction="uploadFileDialog = false" 
		@onPrimaryAction="onUploadFile"></file-upload>

		<embedded-forms-app-dialog
			v-if="embeddedForms.dialog"
			:dialog="embeddedForms.dialog"
			:title="embeddedForms.title"
			:path="embeddedForms.path"
			@close="closeEmbeddedForms"
			@ready="onEmbeddedFormsReady"
			@routeChanged="onEmbeddedFormsRouteChanged"
			@authRequired="onEmbeddedFormsAuthRequired"
			@authLoginStarted="onEmbeddedFormsAuthStarted"
			@authLoginCompleted="onEmbeddedFormsAuthCompleted"
			@authLoginFailed="onEmbeddedFormsAuthFailed"
			@submissionCompleted="onEmbeddedSubmissionCompleted"
		/>
	</div>
</template>

<script>
	import { mapGetters } from 'vuex';
	import avatarMixin from '@/mixins/avatarMixin';
	import EmployeeGoalList from '@/components/employee/EmployeeGoalList';
	import EmployeePositionDescription from '@/components/employee/EmployeePositionDescription';
	import EmployeePositionList from '@/components/employee/EmployeePositionList';
	import EmployeePositionEditor from '@/components/employee/EmployeePositionEditor';
	import LoadingIndicator from '@/components/common/LoadingIndicator';
	import TagItems from '@/components/tags/TagItems';
	import ApprovePositionDescriptionDialog from '@/components/employee/ApprovePositionDescriptionDialog';
	import SendEmailDialog from '@/components/email/SendEmailDialog';
	import positionDescriptionEmailMixin from '@/mixins/positionDescriptionEmailMixin';
	import FileUpload from '@/components/FileUpload';
	import textSnipMixin from '@/mixins/textSnipMixin';
	import ImageInput from '@/components/ImageInput.vue';
	import EmployeeCompliance from '@/components/compliance/EmployeeCompliance';
	import EmployeeUserAccount from '@/components/employee/EmployeeUserAccount';
	import EmployeeDetailsEditor from '@/components/employee/EmployeeDetailsEditor';
	import EmployeeAssignedFormsList from '@/components/forms/EmployeeAssignedFormsList.vue';
	import EmbeddedFormsAppDialog from '@/components/forms/EmbeddedFormsAppDialog.vue';
	import toast from '@/services/toasts/index.js';
	import AssetsList from '@components/assets/AssetsList.vue';
	import CertificationForEmployee from '@components/certifications/CertificationForEmployee.vue';
	import FeatureFlag from '@/enums/featureFlag';
	import PositionStatus from '@/enums/positionStatus';

	export default {
		components: {
			CertificationForEmployee,
			AssetsList,
			EmployeeGoalList,
			EmployeePositionDescription,
			LoadingIndicator,
			EmployeePositionList,
			EmployeePositionEditor,
			TagItems,
			ApprovePositionDescriptionDialog,
			SendEmailDialog,
			FileUpload,
			ImageInput,
			EmployeeCompliance,
			EmployeeUserAccount,
			EmployeeDetailsEditor,
			EmployeeAssignedFormsList,
			EmbeddedFormsAppDialog
		},
		mixins: [avatarMixin, positionDescriptionEmailMixin, textSnipMixin],
		props: {
			id: {
				required: true
			},
			previousAction: {
				type: String,
				required: false
			},
			certificationId: {
				type: String,
				default: null
			}
		},
		data() {
			return {
				tab: null,
				items: ['Personal', 'Position', 'Performance'],
				employee: null,
				goalSavedNotif: false,
				goalDeletedNotif: false,
				isLoading: false,
				showPositionEditor: false,
				editedPosition: null,
				isSavingPosition: false,
				showConfirmDeletePositionDialog: false,
				isDeletingPosition: false,
				positionToDelete: null,
				snackbar: {
					show: false,
					message: '',
					color: undefined,
					buttonColor: 'pink',
					timeout: 6000
				},
				showApprovePositionDialog: false,
				isApprovingPosition: false,
				showSendPositionToEmployeeDialog: false,
				showSendPositionDialog: false,
				positionToSend: null,
				uploadFileDialog: false,
				positionDescriptionJustApproved: false,
				avatar: {
					formData: null,
					imageURL: null
				},
				avatarHover: false,
				employeeComplianceRefreshId: null, // this is a hack to get the component to refresh. Ideally we would use some sort of event bus for this
				form: null,
				showConfirmTerminateDialog: false,
				showMakeAdminDialog: false,
				showRevokeAdminDialog: false,
				embeddedForms: {
					dialog: false,
					title: 'Forms',
					path: ''
				}
			};
		},
		created() {
			this.isLoading = true;
			this.$store.dispatch('setPageTitle', '');

			this.loadEmployee();
			this.loadAssignedForms();

			if (this.previousAction) {
				switch (this.previousAction) {
					case 'employee_created':
						this.showSnackbar(`${this.$term('employee', { case: 'title' })} Created`);
						break;
					case 'employee_updated':
						this.showSnackbar(`${this.$term('employee', { case: 'title' })} Updated`);
						break;
					case 'new_position_draft_created':
						this.showSnackbar('New Position [draft] Created');
						break;
					default:
						break;
				}
			}
		},
		watch: {
			showSendPositionToEmployeeDialog(newVal, oldVal) {
				if (oldVal) this.positionDescriptionJustApproved = false;
			},
			employee(newVal) {
				this.form = this.mapFromEmployeeToForm(newVal);
			},
			'$route.name': {
				immediate: true,
				handler(name) {
					if (name === "employeeCertifications" || name === "employeeCertificationsWithId") {
						this.tab = "certifications";
					}
				}
			},
			'$route.query.tab': {
				immediate: true,
				handler(tab) {
					if (tab === "forms" && this.isFormsEnabled) {
						this.tab = "forms";
					}
				}
			}
		},
		computed: {
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee',
				loggedInTenant: 'loggedInTenant',
				positionTemplates: 'positionTemplates/positionTemplateList',
				employees: 'employees/employeesMiniActiveOnly',
				isTerminatingEmployee: 'employees/isTerminateSaving',
				isSettingAdminPrivileges: 'employees/isSettingAdminPrivileges',
				isListingAssignedForms: 'forms/isListingAssignedToEmployee',
				availableFormsRaw: 'forms/formsList',
				isAssigningForm: 'forms/isCreatingSubmission',
				isSendingNewFormLink: 'forms/isSendingNewLink',
				isCancellingFormSubmission: 'forms/isCancellingSubmission'
			}),
			employeeLoaded() {
				return this.employee !== null;
			},
			isLoggedInEmployee() {
				return this.id == this.loggedInEmployee.id;
			},
			fullName() {
				if (!this.employeeLoaded) return '';
				return `${this.form.employeeDetails.basicInfo.firstName} ${this.form.employeeDetails.basicInfo.lastName}`;
			},
			firstName() {
				if (!this.employeeLoaded) return '';
				return this.form.employeeDetails.basicInfo.firstName;
			},
			emailAddress() {
				if (!this.employeeLoaded) return '';
				return this.form.employeeDetails.work.email;
			},
			department() {
				if (!this.employeeLoaded) return '';
				if (!this.form.employeeDetails.work.department) return '';
				return this.form.employeeDetails.work.department.name;
			},
			tags() {
				if (!this.employeeLoaded) return [];
				return this.form.employeeDetails.tags;
			},
			emailAddressSnipped() {
				return this.$_snipText(this.emailAddress, 28);
			},
			isHiddenFromEmployeeDirectory() {
				if (!this.employeeLoaded) return false;
				return this.form.employeeDetails.otherDetails.hideFromEmployeeDirectory;
			},
			hasLogin() {
				if (!this.employeeLoaded) return false;
				return this.form.employeeDetails.login.userId;
			},
			hasAccess() {
				if (this.employee === null) {
					return true;
				} else {
					const isUnderling = this.loggedInEmployee.underlings != null && this.loggedInEmployee.underlings.includes(this.employee.id);
					return this.isLoggedInEmployee || this.loggedInEmployee.isAdmin || isUnderling;
				}
			},
			goals() {
				return this.employee !== null ? this.employee.goals : [];
			},
			currentPosition() {
				return this.employee !== null && this.employee.currentPosition !== null ? this.employee.currentPosition : null;
			},
			canEdit() {
				const isUnderling = this.loggedInEmployee.underlings != null && this.loggedInEmployee.underlings.includes(this.employee.id);
				return this.hasAccess && (this.loggedInEmployee.isAdmin || isUnderling);
			},
			reportsTo() {
				if (!this.employee) return null;

				return this.$store.getters['employees/getEmployeeMini'](this.employee.reportsTo);
			},
			assignedForms() {
				return this.$store.getters['forms/assignedFormsForEmployee'](this.id);
			},
			availableForms() {
				return this.availableFormsRaw
					.filter(form => !!form && !!form.id && form.status === 'Active')
					.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
			},
			editorTitle() {
				return this.editedPosition == undefined
					? `New ${this.$term('positionDescription', { case: 'title' })}`
					: `Edit ${this.$term('positionDescription', { case: 'title' })}`;
			},
			requireEmployeeAcknowledgement() {
				return !(
					this.loggedInTenant &&
					this.loggedInTenant.tenantSettings &&
					this.loggedInTenant.tenantSettings.requireEmployeeAcknowledgementForPositionDescriptions === false
				);
			},
			positionApprovedMessage() {
				return this.requireEmployeeAcknowledgement
					? `${this.$term('positionDescription', { case: 'title' })} Approved.`
					: `${this.$term('positionDescription', { case: 'title' })} Approved and now Live.`;
			},
			employeeForms() {
				let forms = [{ name: 'basicInfo' }, { name: 'work' }, { name: 'personal' }, { name: 'address' }, { name: 'emergency' }, { name: 'tags' }, { name: 'hrPolicies' }, { name: 'otherDetails' }, { name: 'logins' }];

				if (this.form.status.toLowerCase() === 'inactive') {
					forms = forms.filter(form => form.name !== 'logins');
				}

				if (!this.canEdit) {
					forms = forms.filter(form => form.name !== 'hrPolicies');
				}

				return forms;
			},
			canSchedulePerformanceReview() {
				return this.$_hasAccessToModule('performanceReviews') && !this.isHiddenFromEmployeeDirectory;
			},
			canTerminateEmployee() {
				return !this.isLoggedInEmployee && this.canEdit && this.form.status.toLowerCase() === 'active';
			},
			canMakeAdmin() {
				return this.loggedInEmployee.isAdmin && !this.isLoggedInEmployee && this.hasLogin && !this.form.isAdmin;
			},
			canRevokeAdmin() {
				return this.loggedInEmployee.isAdmin && !this.isLoggedInEmployee && this.hasLogin && this.form.isAdmin;
			},
			isCertificationsEnabled() {
				return this.$store.getters['features/isFeatureEnabled'](FeatureFlag.Certifications);
			},
			isFormsEnabled() {
				return this.$store.getters['features/isFeatureEnabled'](FeatureFlag.Forms);
			},
		},
		methods: {
			mapFromEmployeeToForm(employee) {
				return {
					id: employee.id,
					status: employee.status,
					isActive: employee.status === 'active',
					isAdmin: employee.isAdmin,
					employeeDetails: {
						basicInfo: {
							firstName: employee.firstName,
							lastName: employee.lastName,
							middleName: employee.middleName
						},
						work: {
							department: employee.department,
							email: employee.email,
							phone: employee.workDetails.phone
						},
						personalDetails: {
							prefix: employee.personalDetails.prefix,
							dateOfBirth: {
								date: this.$options.filters.formatDate(employee.personalDetails.dateOfBirth, 'YYYY-MM-DD'),
								menu: false
							},
							nationality: employee.personalDetails.nationality,
							gender: employee.personalDetails.gender,
							phone: employee.personalDetails.phone,
							email: employee.personalDetails.email
						},
						residentialAddresses: employee.residentialAddresses || [],
						emergency: {
							firstName: employee.emergencyContactDetails.firstName,
							lastName: employee.emergencyContactDetails.lastName,
							email: employee.emergencyContactDetails.email,
							phoneNo: employee.emergencyContactDetails.phoneNo,
							relation: employee.emergencyContactDetails.relation
						},
						hrPolicies: [],
						tags: employee.tags,
						otherDetails: {
							hideFromEmployeeDirectory: employee.hideFromEmployeeDirectory
						},
						login: {
							userId: employee.userId
						}
					}
				};
			},
			loadEmployee() {
				if (this.id === this.loggedInEmployee.id) {
					this.$store.dispatch('employees/getLoggedInEmployee').then(
						// Slightly faster as doesn't need to check permissions
						response => {
							this.isLoading = false;
							this.employee = response;
							this.avatar.imageURL = this.getAvatar(this.employee.id);
						},
						error => {
							this.isLoading = false;
						}
					);
				} else {
					this.$store.dispatch('employees/getEmployee', this.id).then(
						response => {
							this.isLoading = false;
							this.employee = response;

							this.avatar.imageURL = this.getAvatar(this.employee.id);
						},
						error => {
							this.isLoading = false;
						}
					);
				}
			},
			loadAssignedForms() {
				this.$store.dispatch('forms/listAssignedToEmployee', this.id);
			},
			async ensureFormsLoaded() {
				const loadTasks = [];

				if (this.availableForms.length === 0) {
					loadTasks.push(this.$store.dispatch('forms/list'));
				}

				if (!this.employees || this.employees.length === 0) {
					loadTasks.push(this.$store.dispatch('employees/initialize'));
				}

				await Promise.all(loadTasks);
			},
			async assignFormToEmployee(assignment) {
				const employeePartyKind = 1;
				const formId = typeof assignment === 'object' ? assignment.formId : assignment;
				const employeeId = typeof assignment === 'object' && assignment.employeeId ? assignment.employeeId : this.id;
				const recipientEmployeeIds = this.getAssignmentRecipientEmployeeIds(assignment);

				const result = await this.$store.dispatch('forms/createSubmission', {
					formId,
					values: {},
					successMessage: 'Form assigned successfully.',
					errorMessage: 'Failed to assign form.',
					parties: {
						subject: {
							kind: employeePartyKind,
							employeeId
						},
						respondent: {
							kind: employeePartyKind,
							employeeId
						},
						recipients: recipientEmployeeIds.map(employeeId => ({
							kind: employeePartyKind,
							employeeId
						}))
					}
				});

				if (!result.isSuccess) return;

				await this.$store.dispatch('forms/listAssignedToEmployee', this.id);

				if (this.$refs.employeeAssignedFormsList) {
					this.$refs.employeeAssignedFormsList.closeDialog();
				}
			},
			getAssignmentRecipientEmployeeIds(assignment) {
				if (!assignment || typeof assignment !== 'object') return [];

				const ids = [];
				const employeeId = assignment.employeeId || this.id;

				if (assignment.notifyManager && this.employee && this.employee.reportsTo) {
					ids.push(this.employee.reportsTo);
				}

				if (Array.isArray(assignment.notifyEmployeeIds)) {
					ids.push(...assignment.notifyEmployeeIds);
				}

				return [...new Set(ids.filter(id => id && id !== employeeId))];
			},
			async sendNewFormLink(item) {
				if (!item || !item.submissionId) return;

				const confirmed = window.confirm('Send a new secure link for this form? The previous link will stop working.');
				if (!confirmed) return;

				const result = await this.$store.dispatch('forms/sendNewLink', {
					employeeId: this.id,
					submissionId: item.submissionId
				});

				if (result.isSuccess) {
					await this.$store.dispatch('forms/listAssignedToEmployee', this.id);
				}
			},
			async cancelFormSubmission(item) {
				if (!item || !item.submissionId) return;

				const confirmed = window.confirm(`Cancel this form assignment? The existing link will stop working and the ${this.$term('employee')} will no longer be able to complete it.`);
				if (!confirmed) return;

				const result = await this.$store.dispatch('forms/cancelSubmission', {
					employeeId: this.id,
					submissionId: item.submissionId
				});

				if (result.isSuccess) {
					await this.$store.dispatch('forms/listAssignedToEmployee', this.id);
				}
			},
			openEmbeddedSubmission({ submissionId, isReadonly }) {
				if (!this.loggedInTenant || !submissionId) return;

				const basePath = `/internal/forms/${this.loggedInTenant.id}/${submissionId}`;
				this.embeddedForms = {
					dialog: true,
					title: isReadonly ? 'View Submission' : 'Complete Form',
					path: isReadonly ? `${basePath}/view` : basePath
				};
			},
			closeEmbeddedForms() {
				this.embeddedForms.dialog = false;
			},
			onEmbeddedFormsReady() {
				// Loader state is managed within the embedded host component.
			},
			onEmbeddedFormsRouteChanged(payload) {
				if (!payload || !payload.name) return;
			},
			onEmbeddedFormsAuthStarted() {
				this.showSnackbar('Signing you in to forms...');
			},
			onEmbeddedFormsAuthRequired() {
				// Child app will attempt silent SSO and prompt only if needed.
			},
			onEmbeddedFormsAuthCompleted() {
				// Embedded host clears its own auth overlay.
			},
			onEmbeddedFormsAuthFailed(payload) {
				this.showSnackbar(payload && payload.message ? payload.message : 'Unable to sign you in to forms.');
			},
			async onEmbeddedSubmissionCompleted() {
				await this.$store.dispatch('forms/listAssignedToEmployee', this.id);
				this.showSnackbar('Form submission completed');
				this.closeEmbeddedForms();
			},
			onEditEmployeeClick() {
				this.$router.push({
					name: 'employeeEdit',
					params: { id: this.id }
				});
			},
			downloadFile(id, fileName) {
				this.$store.dispatch('employees/downloadDocument', {
					employeeId: this.id,
					id: id,
					fileName: fileName
				});
			},
			onGoalAdded({ employeeId, goal }) {
				this.employee.goals.push(goal);
				this.goalSavedNotif = true;
			},
			onGoalEdited({ employeeId, goal }) {
				let foundGoal = this.employee.goals.find(x => x.id === goal.id);
				Object.assign(foundGoal, goal);
				this.goalSavedNotif = true;
			},
			onGoalDeleted({ employeeId, id }) {
				const index = this.employee.goals.indexOf(this.employee.goals.filter(x => x.id == id));
				this.employee.goals.splice(index, 1);
				this.goalDeletedNotif = true;
			},
			onAddPosition() {
				this.editedPosition = undefined;
				this.showPositionEditor = true;
			},
			onEditPosition(position) {
				this.editedPosition = position;
				this.showPositionEditor = true;
			},
			onDeletePosition(position) {
				this.positionToDelete = position;
				this.showConfirmDeletePositionDialog = true;
			},
			deletePosition() {
				this.isDeletingPosition = true;

				this.$store.dispatch('employees/deletePosition', { employeeId: this.employee.id, positionId: this.positionToDelete.id }).then(
					response => {
						this.isDeletingPosition = false;
						this.showConfirmDeletePositionDialog = false;

						// Remove position
						const foundIndex = this.employee.positions.findIndex(x => x.id === this.positionToDelete.id);
						this.employee.positions.splice(foundIndex, 1);

						this.positionToDelete = null;

						this.showSnackbar('Position Deleted');
					},
					error => {
						this.isDeletingPosition = false;
						this.showSnackbar('The Position could not be deleted at this time');
					}
				);
			},
			async terminateEmployee() {
				const result = await this.$store.dispatch('employees/terminate', {
					employeeId: this.employee.id,
					reason: ''
				});

				if (result.isSuccess) {
					this.form.status = 'inactive';
					this.form.isActive = false;
					this.form.employeeDetails.login = {
						userId: null
					};
					this.showConfirmTerminateDialog = false;

					toast.success(`${this.$term('employee', { case: 'title' })} terminated`);
				}
			},
			async makeAdmin() {
				const result = await this.$store.dispatch('employees/setAdminPrivileges', {
					employeeId: this.employee.id,
					value: true
				});

				if (result.isSuccess) {
					this.form.isAdmin = true;
					this.showMakeAdminDialog = false;

					toast.success(`${this.$term('employee', { case: 'title' })} admin privileges have been granted`);
				}
			},
			async revokeAdminPrivileges() {
				const result = await this.$store.dispatch('employees/setAdminPrivileges', {
					employeeId: this.employee.id,
					value: false
				});

				if (result.isSuccess) {
					this.form.isAdmin = false;
					this.showRevokeAdminDialog = false;

					toast.success(`${this.$term('employee', { case: 'title' })} admin privileges have been revoked`);
				}
			},
			savePosition(position) {
				this.isSavingPosition = true;

				if (this.editedPosition === undefined) {
					this.$store
						.dispatch('employees/addPosition', {
							employeeId: this.id,
							position
						})
						.then(
							updatedEmployee => {
								this.isSavingPosition = false;
								this.employee = updatedEmployee;
								this.showSnackbar('New Position Added');
								this.showPositionEditor = false;
							},
							error => {
								this.isSavingPosition = false;
								this.showPositionEditor = false;
								this.showSnackbar(`The ${this.$term('positionDescription', { case: 'title' })} could not be added at this time`, true);
							}
						);
				} else {
					const newDraftToBeCreated = position.triggerApprovalWorkflow && this.editedPosition.status === PositionStatus.Live;

					this.$store
						.dispatch('employees/updatePosition', {
							employeeId: this.id,
							position
						})
						.then(
							updatedEmployee => {
								this.isSavingPosition = false;
								this.employee = updatedEmployee;
								this.showPositionEditor = false;

								if (newDraftToBeCreated) {
									this.showSnackbar('New Position [draft] Created');
								} else {
									this.showSnackbar('Position Updated');
								}
							},
							error => {
								this.isSavingPosition = false;
								this.showPositionEditor = false;
								this.showSnackbar(`The ${this.$term('positionDescription', { case: 'title' })} could not be updated at this time`, true);
							}
						);
				}
			},
			convertPositionToTemplate(template) {
				this.$store.dispatch('positionTemplates/add', template).then(
					response => {
						this.showSnackbar(`${this.$term('positionTemplate', { case: 'title' })} Created`);
					},
					error => {
						this.showSnackbar(`The ${this.$term('positionTemplate', { case: 'title' })} could not be created at this time`);
					}
				);
			},
			showSnackbar(message, isError) {
				this.snackbar.show = true;
				this.snackbar.message = message;
				this.snackbar.color = isError ? 'error' : undefined;
				this.snackbar.buttonColor = isError ? '#fff' : 'pink';
				this.snackbar.timeout = isError ? 0 : 6000;
			},
			onApprovePosition(position) {
				this.positionToApprove = position;
				this.showApprovePositionDialog = true;
			},
			approvePosition() {
				this.isApprovingPosition = true;
				this.$store.dispatch('employees/approvePosition', { employeeId: this.id, positionId: this.positionToApprove.id }).then(
					updatedEmployee => {
						this.showSnackbar(this.positionApprovedMessage);
						this.employee = updatedEmployee;
						this.isApprovingPosition = false;
						this.showApprovePositionDialog = false;
						this.positionDescriptionJustApproved = true;

						this.employeeComplianceRefreshId = this.newGuid(); // Trigger refresh of compliance

						this.positionToSend = updatedEmployee.positions.find(x => x.id === this.positionToApprove.id);
						this.showSendPositionToEmployeeDialog = true;
					},
					error => {
						this.showApprovePositionDialog = false;
						this.isApprovingPosition = false;
						this.showSnackbar(`The ${this.$term('positionDescription', { case: 'title' })} could not be approved at this time`, true);
					}
				);
			},
			onSendPosition(position) {
				this.positionToSend = position;
				this.showSendPositionDialog = true;
			},
			onSendPositionToEmployee(position) {
				this.positionToSend = position;
				this.showSendPositionToEmployeeDialog = true;
			},
			onPositionDescriptionSentToEmployee() {
				this.showSendPositionToEmployeeDialog = false;
				this.showSnackbar(`${this.$term('positionDescription', { case: 'title' })} sent to ${this.$term('employee', { case: 'title' })}`);
			},
			onPositionDescriptionSent() {
				this.showSendPositionDialog = false;
				this.showSnackbar(`${this.$term('positionDescription', { case: 'title' })} sent`);
			},
			onUploadFileClick() {
				this.uploadFileDialog = true;
			},
			onUploadFile(formData) {
				this.$store
					.dispatch('employees/uploadDocument', {
						employeeId: this.id,
						document: formData
					})
					.then(
						newDocuments => {
							this.uploadFileDialog = false;
							this.showSnackbar('File Uploaded Successfully');

							newDocuments.forEach(document => {
								this.employee.files.push(document);
							});
						},
						error => {
							console.error('Error uploading document');
						}
					);
			},
			onDeleteFile(id) {
				if (confirm('Are you sure you want to delete this document?')) {
					this.$store
						.dispatch('employees/deleteDocument', {
							employeeId: this.id,
							id: id
						})
						.then(
							response => {
								this.showSnackbar('File Deleted Successfully');
								const index = this.employee.files.indexOf(this.employee.files.find(x => x.id == id));
								if (index > -1) {
									this.employee.files.splice(index, 1);
								}
							},
							error => {
								console.error('Error deleting document');
							}
						);
				}
			},
			schedulePerformanceReview() {
				this.$router.push({
					name: 'prCreate',
					params: { employeeId: this.id }
				});
			},
			uploadImage() {
				this.$store
					.dispatch('employees/uploadAvatar', {
						employeeId: this.id,
						file: this.avatar.formData
					})
					.then(
						response => {
							this.showSnackbar('Profile Image Updated');
						},
						error => {
							this.showSnackbar('Profile Image could not be updated at this time');
						}
					);
			},
			onComplianceUpdated() {
				this.loadEmployee();
			}
		}
	};
</script>

<style scoped>
	.v-tabs__div a:hover i {
		text-decoration: none;
	}
	.v-sheet {
		border-radius: 0;
	}
	.profile-container {
		min-height: calc(100vh - 280px);
	}
	.profile-body {
		height: 100%;
		margin-top: -50px;
	}
	.profile-body-sidebar {
		width: 250px;
		background-color: rgb(244 244 244);
		padding-top: 40px;
	}
	.profile-tab {
	}
	.profile-header {
		background-color: #2196f3;
		padding-top: 12px;
		padding-bottom: 24px;
	}
	.avatar-hover {
		position: absolute;
		top: 0px;
		bottom: 0px;
		left: 0px;
		right: 0px;
		color: #fff;
		background-color: rgba(0, 0, 0, 0.6);
		border-radius: 50%;
	}
	.avatar-hover span {
		position: absolute;
		top: 40px;
		right: 1px;
		color: #fff;
	}
	.info-text {
		color: rgba(0, 0, 0, 0.54);
		font-size: 12px;
		font-style: italic;
	}
</style>
